import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonContent,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    IonPage,
    isPlatform
} from "@ionic/react";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { debounce } from "lodash";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { generatePath, RouteComponentProps, useHistory, useLocation } from "react-router";
import { Screens, useAnalytics } from "../AppAnalyticsProvider";
import { useAppState } from "../AppListenerProvider";
import { AppContext } from "../AppStateProvider";
import { useAuth } from "../AuthProvider";
import CustomHelmet from "../components/ComponentsFunctional/CustomHelmet/CustomHelmet";
import LoginButton from "../components/ComponentsLogin/LoginButton/LoginButton";
import WorldWeb from '../components/ComponentsMap/WorldWeb/WorldWeb';
import ExitFilterButton from "../components/ComponentsSearch/ExitFilterButton/ExitFilterButton";
import FeatureSelect from "../components/ComponentsSearch/FeatureSelect/FeatureSelect";
import FilterToolbar from "../components/ComponentsSearch/FilterToolbar/FilterToolbar";
import FilterTrickButton from "../components/ComponentsSearch/FilterTricksButton/FilterTricksButton";
import SearchBarFilter from "../components/ComponentsSearch/SearchBarFilter/SearchBarFilter";
import SportSelect from "../components/ComponentsSearch/SportSelect/SportSelect";
import TrickSearchCardHolder from "../components/ComponentsSearch/TrickSearchCard/TrickSearchCardHolder";
import VirtualProfileSearchList from "../components/ComponentsSearch/VirtualProfileSearchList/VirtualProfileSearchList";
import UserListSkeleton from "../components/ComponentsUI/SearchProfilesLoading/SearchProfilesLoading";
import SearchTricksLoading from "../components/ComponentsUI/SearchTricksLoading/SearchTricksLoading";
import ToolbarSearchBar from "../components/ComponentsUI/ToolbarSearchBar/ToolbarSearchBar";
import TrickAttributeSelect from "../components/ComponentsUI/TrickAttributeSelect/TrickAttributeSelect";
import TrickDropdown from "../components/ComponentsUI/TrickDropdown/TrickDropdown";
import { useProfileSearch } from "../hooks/useProfileSearch";
import { getAdjustedTiming, QueryTiming } from "../hooks/useQueryConfig";
import { useRevenueCatPaywall } from "../hooks/useRevenueCatPaywall";
import { getRandomSkiMountainCoordinates } from "../hooks/useTrickLocation";
import { LocationTrick } from "../models/locationTrick";
import { SportType } from "../models/sportType";
import { User } from "../models/user";
import { UserProfileSearch } from "../models/user-profile-search";
import { UserTrickSearch, UserTrickSearch as UserTrickSearchModel } from "../models/user-trick-search";
import { UserTrickSearchMetadata } from "../models/userTrickSearchMetadata";
import { UserType } from "../models/usertype";
import BiskService from "../services/bisk.service";
import LocationService from "../services/location.service";
import PreferencesService from "../services/preferences.service";
import SearchService from "../services/search.service";
import TricksService from "../services/tricks.service";
import UserService from "../services/user.service";
import { filterTricksForBlockedIds } from "../services/utils";


interface FilterParams {
    query?: string;
}

interface FilterPageProps extends RouteComponentProps<FilterParams> { }

const Filter: React.FC<FilterPageProps> = ({ match }) => {
    // ROUTE HOOKS
    const history = useHistory();
    const location = useLocation();
    const { query } = match.params;
    const { trackEvent, trackScreen } = useAnalytics();

    // CONTEXT HOOKS
    const { state } = useContext(AppContext);
    const { isAuthenticated, isLoading, user } = useAuth();
    const { isNative, platform } = useAppState();
    const queryClient = useQueryClient();

    // STATE HOOKS
    const [showFilterOverlay, setShowFilterOverlay] = useState(false); // shows the filter buttons

    // search for tricks by trick name or riders doing them (ie: I search for A Hall and get A Hall tricks)
    const [filterSearch, setFilterSearch] = useState("");
    const [trickSearch, setTrickSearch] = useState<string>("");
    const [trickSearchResults, setTrickSearchResults] = useState<UserTrickSearchMetadata[]>([]);
    const [exploreTrickSearchResults, setExploreTrickSearchResults] = useState<UserTrickSearchMetadata[]>([]);

    // for filtering
    const [sport, setSport] = useState("");
    const [sportDisplay, setSportDisplay] = useState("");
    const [feature, setFeature] = useState("");
    const [featureSearchValue, setFeatureSearchValue] = useState("");
    const [trickAttribute, setTrickAttribute] = useState("");
    const [filteredTricks, setFilteredTricks] = useState([]);

    const [showFeatureOptions, setShowFeatureOptions] = useState(false);
    const [showTrickAttributeOptions, setShowTrickAttributeOptions] = useState(false);
    const [showTricks, setShowTricks] = useState(false);
    const [initLoading, setInitLoading] = useState(false);

    const [pageExplore, setPageExplore] = useState(0);

    // for pagination
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(18);
    const [searchTricksInfiniteScrollDisabled, setSearchTricksInfiniteScrollDisabled] = useState(false);
    const [isLoadingMoreTricks, setIsLoadingMoreTricks] = useState(false);

    const [displayClips, setDisplayClips] = useState<boolean>(true);
    const [displayProfiles, setDisplayProfiles] = useState<boolean>(false);
    const [displayMap, setDisplayMap] = useState<boolean>(false);

    // Map-specific state
    const randomMountain = useMemo(() => getRandomSkiMountainCoordinates(), []);
    const [baseLat] = useState<number>(randomMountain.latitude);
    const [baseLong] = useState<number>(randomMountain.longitude);
    const [baseZoom] = useState<number>(9);
    const [blurredMap, setBlurredMap] = useState<boolean | undefined>(undefined);
    const [mapBounds, setMapBounds] = useState<{
        ne_lat: number;
        ne_long: number;
        sw_lat: number;
        sw_long: number;
    } | null>(null);
    const [flyToLocation, setFlyToLocation] = useState<number[] | undefined>(undefined);
    const [flyToZoom, setFlyToZoom] = useState<number | undefined>(undefined);

    // User location state to persist across tab switches
    const [userLocation, setUserLocation] = useState<{
        lat: number;
        lng: number;
        hasLocation: boolean;
    }>({
        lat: randomMountain.latitude,
        lng: randomMountain.longitude,
        hasLocation: false
    });

    // 4. Query hooks
    const {
        data: profileData,
        isLoading: isLoadingProfiles,
        fetchNextPage: fetchNextProfilePage,
        hasNextPage: hasNextProfilePage
    } = useProfileSearch(trickSearch);

    const {
        presentPaywall: presentTowRopePaywall,
    } = useRevenueCatPaywall({
        userId: state.user.id,
        userBisk: state.bisk,
        username: state.user.username
    });

    // Map-related queries (similar to Map.tsx)
    const { data: isSubscribed, isLoading: subscriptionLoading } = useQuery({
        queryKey: ['isSubscribed'],
        queryFn: async () => {
            const auth_id = await PreferencesService.getUserUid();
            if (auth_id) {
                try {
                    const user: User = await UserService.getUserByAuthId(auth_id);
                    return await BiskService.getIsSubscribedById(user.id);
                } catch (e) {
                    return false;
                }
            }
            return false;
        },
        enabled: isAuthenticated && displayMap,
    });

    const { data: authInfo } = useQuery({
        queryKey: ['authInfo', isSubscribed],
        queryFn: async () => {
            const auth_id = await PreferencesService.getUserUid();
            if (auth_id) {
                const userType = await UserService.getUserType(auth_id);
                if (userType === UserType.USER) {
                    const response: User = await UserService.getUserByAuthId(auth_id);
                    if (response) {
                        const userTricks = await TricksService.getUserTricks(response.id);
                        const isUserGod = (await UserService.getIsUserGod(auth_id)).result;
                        return { isUserGod, userTricksCount: userTricks.length };
                    }
                }
                return { userType };
            }
            return null;
        },
        enabled: isAuthenticated && !isLoading && displayMap,
    });

    const handleBoundsChange = (bounds: {
        ne_lat: number;
        ne_long: number;
        sw_lat: number;
        sw_long: number;
    }) => {
        setMapBounds(bounds);
    };

    const { data: geoJson, isLoading: isGeoJsonLoading } = useQuery({
        queryKey: ['mapTricks', mapBounds, isSubscribed],
        queryFn: async () => {
            let res: LocationTrick[];

            if (mapBounds) {
                res = await LocationService.getAllLocationTricksWithinBound(
                    mapBounds.ne_lat,
                    mapBounds.ne_long,
                    mapBounds.sw_lat,
                    mapBounds.sw_long
                );
            } else {
                res = await LocationService.getAllLocationTricks();
            }

            const resGeoJsonHolder: any = {
                'type': 'FeatureCollection',
                'features': []
            };

            if (res) {
                const trick_ids = res.map((trick) => trick.trick_id);
                const user_tricks: UserTrickSearchModel[] = await UserService.getTricksFromIds(trick_ids);

                resGeoJsonHolder.features = res.map(trickLocation => {
                    const trick = user_tricks.find((t) => t.trick_id === trickLocation.trick_id);
                    if (trick) {
                        let long = isSubscribed ? (trickLocation.long || trickLocation.google_long) : trickLocation.google_long;
                        let lat = isSubscribed ? (trickLocation.lat || trickLocation.google_lat) : trickLocation.google_lat;

                        const mapThumbUrl = trick.thumbnail.replace("d3btq01zzysv3d.cloudfront.net", "d2xrbrtg3kyq6g.cloudfront.net");

                        return {
                            'type': 'Feature',
                            'properties': {
                                'iconSize': [10, 10],
                                'creation_date': trick.date,
                                'image': mapThumbUrl,
                                'trick': trick,
                                'id': trick.id,
                            },
                            'geometry': {
                                'type': 'Point',
                                'coordinates': [long, lat]
                            }
                        };
                    }
                    return null;
                }).filter(Boolean);
            }

            return resGeoJsonHolder;
        },
        enabled: isAuthenticated && !isLoading && isSubscribed !== undefined && displayMap,
        ...getAdjustedTiming(QueryTiming.MEDIUM_FREQUENCY),
    });

    // 5. Memoized values
    const profileSearchResults = useMemo(() =>
        profileData?.pages?.flatMap(page => page.profiles) ?? [],
        [profileData]
    );

    // Helper function to format search terms for display
    const getSearchDisplayText = (): string => {
        const parts: string[] = [];

        // Add filter tags
        if (filteredTricks.length > 0) {
            parts.push(...filteredTricks.map(t => t.replace('_', ' ')));
        }

        // Add feature
        if (featureSearchValue) {
            parts.push(featureSearchValue);
        }

        // Add sport
        if (sportDisplay) {
            parts.push(sportDisplay);
        }

        // Add search text
        if (trickSearch) {
            parts.push(trickSearch);
        }

        if (parts.length === 0) {
            return "your search";
        }

        return parts.join(', ');
    };

    // USE EFFECT
    // Track initial screen view. This surface is the /search route — use the
    // canonical `search` screen name (Screens.SEARCH) so it appears in the
    // engagement dashboard as expected. Previously fired `'filter'` which
    // meant search usage was invisible in top-screens reporting.
    useEffect(() => {
        trackScreen(Screens.SEARCH, 'FilterScreen');
        trackEvent('filter_screen_viewed', {
            is_authenticated: isAuthenticated,
            display_clips: displayClips,
            display_riders: displayProfiles
        });
    }, []);

    useEffect(() => {
        if (isPlatform("capacitor") || isPlatform("mobile")) {
            setPageSize(10);
        }
    }, []);

    useEffect(() => {
        setDisplayClips(true);
        setDisplayProfiles(false);
        setDisplayMap(false);
    }, []);

    // Clear search when switching TO the map tab
    useEffect(() => {
        if (displayMap) {
            setTrickSearch("");
            setFilterSearch("");
        }
    }, [displayMap]);

    // Reset flyTo coordinates after they've been used
    useEffect(() => {
        if (flyToLocation) {
            const timer = setTimeout(() => {
                setFlyToLocation(undefined);
                setFlyToZoom(undefined);
            }, 1000); // Reset after 1 second to allow animation to complete
            return () => clearTimeout(timer);
        }
    }, [flyToLocation]);

    // Map blur logic
    useEffect(() => {
        if (displayMap && authInfo) {
            if (authInfo.userType === UserType.BRAND || authInfo.isUserGod || isSubscribed || authInfo.userTricksCount > 0) {
                setBlurredMap(false);
            } else {
                setBlurredMap(true);
            }
        } else if (displayMap && isAuthenticated === false) {
            setBlurredMap(true);
        }
    }, [authInfo, isAuthenticated, isSubscribed, displayMap]);

    useEffect(() => {
        if (query) {
            console.log("replacing page with populated query")
            // Add query as a filter tag instead of putting it in the search bar
            const queryTag = query.trim();
            if (queryTag && !filteredTricks.includes(queryTag)) {
                const newFilteredTricks = [...filteredTricks, queryTag];
                setFilteredTricks(newFilteredTricks);
                // Perform search with the query tag
                filterTricks(sport, sportDisplay, featureSearchValue, "", newFilteredTricks, showFilterOverlay, showFeatureOptions, showTrickAttributeOptions);
            }
        } else {
            console.log("replacing page with blank query")
            history.replace({
                pathname: generatePath(match.path, { query: '' }),
            });
            setInitExploreTricks();
        }
        console.log("Filter page loaded");
    }, [history, query]);

    useEffect(() => {

    }, [state.user.profile, state.user.id]);

    // Track errors and loading states
    useEffect(() => {
        if (initLoading) {
            trackEvent('filter_loading_state', {
                search_term: trickSearch,
                filter_search: filterSearch,
                has_results: trickSearchResults?.length > 0
            });
        }
    }, [initLoading]);


    // Track display toggles
    const onSelectClips = () => {
        trackEvent('filter_display_changed', {
            display_type: 'clips',
            previous_display: displayClips ? 'clips' : (displayProfiles ? 'riders' : 'map')
        });
        setDisplayClips(true);
        setDisplayProfiles(false);
        setDisplayMap(false);
    };

    const onSelectRiders = () => {
        trackEvent('filter_display_changed', {
            display_type: 'riders',
            previous_display: displayClips ? 'clips' : (displayProfiles ? 'riders' : 'map')
        });
        setDisplayClips(false);
        setDisplayProfiles(true);
        setDisplayMap(false);
    };

    const onSelectMap = () => {
        trackEvent('filter_display_changed', {
            display_type: 'map',
            previous_display: displayClips ? 'clips' : (displayProfiles ? 'riders' : 'map')
        });
        setDisplayClips(false);
        setDisplayProfiles(false);
        setDisplayMap(true);
    };

    const handleSearch = useMemo(
        () => debounce((search: string) => {
            trackEvent('filter_profile_search', {
                search_term: search,
                is_authenticated: isAuthenticated
            });
            setTrickSearch(search);
        }, 300), // 300ms delay
        [] // Empty deps array since we don't want to recreate the debounced function
    );

    const onClickPersonProfile = (user: UserProfileSearch) => {
        history.push(`/profile/${user.username}`);
    }

    const onTrickButtonClick = (trick_id: string, user_id: string) => {
        history.push('/clip/' + user_id + "/" + trick_id);
    }

    const onGoToShop = () => {
        return presentTowRopePaywall();
    }

    const reloadAuthInfo = () => {
        queryClient.invalidateQueries({ queryKey: ['authInfo'] });
    }

    const handleUserLocationUpdate = (coords: { latitude: number; longitude: number }) => {
        setUserLocation({
            lat: coords.latitude,
            lng: coords.longitude,
            hasLocation: true
        });
    };

    // Get the coordinates to use for the map center
    const getMapCoordinates = () => {
        if (userLocation.hasLocation) {
            return {
                lat: userLocation.lat,
                lng: userLocation.lng
            };
        }
        return {
            lat: baseLat,
            lng: baseLong
        };
    };

    const processExploreTricksForBlockedUsers = async (initialTricks: UserTrickSearchMetadata[]) => {
        let tricks = initialTricks;
        let processedTricks: UserTrickSearchMetadata[] = [];

        // Continue the loop until you find non-blocked tricks or run out of new tricks to process.
        while (tricks.length > 0) {
            // Filter the tricks based on blocked users.
            processedTricks = filterTricksForBlockedIds(tricks, state.blockedUsers, state.blockingUsers);

            // If some non-blocked tricks are found, break the loop.
            if (processedTricks.length > 0) {
                break;
            }

            // Fetch more tricks if all current ones are blocked.
            tricks = await SearchService.getExploreTricks(
                pageSize,
                pageExplore + 1
            );

            setPageExplore(pageExplore + 1);

            if (tricks.length === 0) {
                break;
            }

            tricks = filterTricksForBlockedIds(tricks, state.blockedUsers, state.blockingUsers);
        }

        return processedTricks;
    }

    const getInitExploreTricks = async () => {
        let initExploreTricks: UserTrickSearchMetadata[] = await SearchService.getExploreTricks(pageSize, pageExplore);
        initExploreTricks = await processExploreTricksForBlockedUsers(initExploreTricks);
        setPageExplore(pageExplore + 1);
        return initExploreTricks;
    }

    const setInitExploreTricks = async () => {
        setInitLoading(true);
        let newSearchTricks = await getInitExploreTricks();
        newSearchTricks = await processExploreTricksForBlockedUsers(newSearchTricks);
        setExploreTrickSearchResults(newSearchTricks);
        setInitLoading(false);
        window.prerenderReady = true;
    }

    const processTrickSearchForBlockedUsers = async (initialTricks: UserTrickSearchMetadata[]) => {
        let tricks = initialTricks;
        let processedTricks: UserTrickSearchMetadata[] = [];

        // Continue the loop until you find non-blocked tricks or run out of new tricks to process.
        while (tricks.length > 0) {
            // Filter the tricks based on blocked users.
            processedTricks = filterTricksForBlockedIds(tricks, state.blockedUsers, state.blockingUsers);

            // If some non-blocked tricks are found, break the loop.
            if (processedTricks.length > 0) {
                break;
            }

            // Fetch more tricks if all current ones are blocked.
            tricks = await SearchService.searchUserTrickPaginated(
                trickSearch,
                pageSize,
                page,
                state.user.id
            );

            setPage(page + 1);

            if (tricks.length === 0) {
                break;
            }

            tricks = filterTricksForBlockedIds(tricks, state.blockedUsers, state.blockingUsers);
        }

        return processedTricks;
    }

    // Location search for map
    const onLocationSearch = async (query: string) => {
        trackEvent('filter_location_search', {
            search_term: query,
            is_authenticated: isAuthenticated
        });

        try {
            const res = await LocationService.getLocationFromTextPlacesAPI(query);
            if (res && res.result && res.result.geometry && res.result.geometry.location) {
                const searched_location = res.result.geometry.location;
                const lat = parseFloat(searched_location.lat);
                const lng = parseFloat(searched_location.lng);

                // Update user location to center map on searched location
                setUserLocation({
                    lat: lat,
                    lng: lng,
                    hasLocation: true
                });

                // Set fly-to coordinates with location search zoom (more detailed)
                setFlyToLocation([lng, lat]);
                setFlyToZoom(13); // Street/neighborhood level for location searches

                console.log(`Location search: Found ${res.result.name} at (${lat}, ${lng})`);
            } else {
                console.warn('Location search returned no results for:', query);
            }
        } catch (error) {
            console.error('Location search failed:', error);
        }
    };

    // const onLocationSearch = async (query: string) => {
    //     const res = await LocationService.getLocationFromTextPlacesAPI(query);
    //     if (res) {
    //         const searched_location = res.result.geometry.location;
    //         map.current!.jumpTo({
    //             center: [parseFloat(searched_location.lng), parseFloat(searched_location.lat)],
    //             essential: true,
    //             zoom: 12
    //         });
    //     }
    // };

    const onTrickSearch = async (search: string) => {
        trackEvent('filter_trick_search', {
            search_term: search,
            is_authenticated: isAuthenticated
        });
        setInitLoading(true);
        setTrickSearch(search);
        setSearchTricksInfiniteScrollDisabled(false);
        setTrickSearchResults([]);
        setPage(0);

        // Build tags array from existing filters
        const searchSport = sport != "" ? sportDisplay : "";
        const searchFeature = featureSearchValue != "" ? featureSearchValue : "";
        const searchTags = filteredTricks.length > 0 ? filteredTricks.map((x) => x.replace("_", " ")) : [];

        // Combine all tags into a single array
        const allTags = [
            ...searchTags,
            ...(searchFeature ? [searchFeature] : []),
            ...(searchSport ? [searchSport] : [])
        ].filter(tag => tag && tag.trim() !== "");

        let res: UserTrickSearchMetadata[];

        // Determine which API to call based on presence of tags
        if (allTags.length > 0) {
            // Use tags API with optional text
            const textParam = search && search.trim() !== "" ? search.trim() : null;
            console.log("Searching with tags:", allTags, "and text:", textParam);
            res = await SearchService.searchUserTrickWithTags(allTags, textParam, pageSize, 0, state.user.id);
            // Set filterSearch to indicate we're using tag-based search
            setFilterSearch(JSON.stringify({ tags: allTags, text: textParam }));
        } else if (search && search.trim() !== "") {
            // Use regular text-only API
            console.log("Searching for text only:", search);
            res = await SearchService.searchUserTrickPaginated(search, pageSize, 0, state.user.id);
            setFilterSearch("");
        } else {
            // No search criteria
            res = [];
            setFilterSearch("");
        }

        res = await processTrickSearchForBlockedUsers(res || []);
        setTrickSearchResults(res);
        setShowFilterOverlay(false);
        setShowFeatureOptions(false);
        setShowTrickAttributeOptions(false);
        setPage(1); // finished getting page 0, now on page 1
        setInitLoading(false);
        window.prerenderReady = true;
    };

    const getMoreSearchTricks = async () => {
        if (searchTricksInfiniteScrollDisabled || isLoadingMoreTricks) return;

        setIsLoadingMoreTricks(true);
        trackEvent('filter_load_more', {
            current_page: page,
            search_type: filterSearch ? 'filter' : trickSearch ? 'search' : 'explore',
            current_results: trickSearchResults?.length
        });

        console.log("Getting more search tricks");
        let newSearchTricks = [];
        let usingExploreTricks = false;

        // Check if filterSearch contains tags-based search (JSON format)
        if (filterSearch && filterSearch.startsWith('{')) {
            try {
                const searchParams = JSON.parse(filterSearch);
                newSearchTricks = await SearchService.searchUserTrickWithTags(
                    searchParams.tags,
                    searchParams.text,
                    pageSize,
                    page,
                    state.user.id
                );
            } catch (e) {
                // Fallback to regular search if JSON parsing fails
                newSearchTricks = await SearchService.searchUserTrickPaginated(filterSearch, pageSize, page, state.user.id);
            }
        } else if (trickSearch) {
            newSearchTricks = await SearchService.searchUserTrickPaginated(trickSearch, pageSize, page, state.user.id);
        } else {
            usingExploreTricks = true;
            newSearchTricks = await getInitExploreTricks();
        }

        if (!newSearchTricks || newSearchTricks.length === 0) {
            setSearchTricksInfiniteScrollDisabled(true);
        } else {
            if (usingExploreTricks) {
                newSearchTricks = await processExploreTricksForBlockedUsers(newSearchTricks);
                setExploreTrickSearchResults([
                    ...exploreTrickSearchResults,
                    ...newSearchTricks
                ]);
                setPageExplore(pageExplore + 1);
            } else {
                newSearchTricks = await processTrickSearchForBlockedUsers(newSearchTricks);
                setTrickSearchResults([
                    ...trickSearchResults,
                    ...newSearchTricks
                ]);
                setPage(page + 1);
            }
        }

        setIsLoadingMoreTricks(false);
        console.log('Loaded more search tricks');
    }

    const onTrickSelection = (trick: UserTrickSearch): void => {
        trackEvent('filter_trick_selected', {
            trick_id: trick.id,
            user_id: trick.user_id,
            from_search: Boolean(trickSearch),
            from_filter: Boolean(filterSearch)
        });
        history.push('/clip/' + trick.user_id + '/' + trick.id);
    }

    // i don't think we need a deselect function for sport for now as we can just reset the sport state
    const onFeatureSelect = async (feature: string, featureSearchValue: string) => {
        trackEvent('filter_feature_selected', {
            feature: feature,
            search_value: featureSearchValue,
            sport: sport
        });

        const filteredFeature = feature;
        const filteredFeatureSearchValue = featureSearchValue
        // Preserve existing filter tags (including query tags)
        const filteredTricksFilter: string[] = [...filteredTricks];
        setFeature(filteredFeature);
        setFeatureSearchValue(filteredFeatureSearchValue);
        console.log("Feature: " + filteredFeature);
        // Don't clear filteredTricks - preserve existing tags
        // TODO: not sure if we need any more logic here
        await filterTricks(sport, sportDisplay, filteredFeatureSearchValue, trickSearch, filteredTricksFilter, showFilterOverlay, showFeatureOptions, showTrickAttributeOptions);
    }

    const onTrickAttributeSelect = (trickAttribute: string) => {
        trackEvent('filter_trick_attribute_selected', {
            attribute: trickAttribute,
            sport: sport,
            feature: feature
        });
        setTrickAttribute(trickAttribute);
    };

    // TODO: you might need to change addTrickToFilter to removeTrickFromFilter based on user interactions
    // could be some bugs (happened to me w/the multiselect with dropdown component)
    const addTrickToFilter = async (trick: string) => {
        trackEvent('filter_trick_added', {
            trick: trick,
            current_filters: filteredTricks,
            sport: sport,
            feature: feature
        });

        const filteredTricksFilter = [...filteredTricks, trick];
        console.log("Adding trick to filter: " + trick);
        setFilteredTricks(filteredTricksFilter);
        console.log("Filtered tricks: " + filteredTricksFilter)
        await filterTricks(sport, sportDisplay, featureSearchValue, trickSearch, filteredTricksFilter, showFilterOverlay, showFeatureOptions, showTrickAttributeOptions);
    }

    const removeTrickFromFilter = async (trick: string) => {
        trackEvent('filter_trick_removed', {
            trick: trick,
            remaining_filters: filteredTricks.filter(t => t !== trick),
            sport: sport,
            feature: feature
        });

        const filteredTricksFilter = filteredTricks.filter((t) => t !== trick);
        console.log("Removing trick from filter: " + trick);
        setFilteredTricks(filteredTricksFilter);
        await filterTricks(sport, sportDisplay, featureSearchValue, trickSearch, filteredTricksFilter, showFilterOverlay, showFeatureOptions, showTrickAttributeOptions);
    }

    const clearFilters = () => {
        trackEvent('filter_cleared', {
            previous_sport: sport,
            previous_feature: feature,
            previous_tricks: filteredTricks
        });

        setSport("");
        setSportDisplay("");
        setFeature("");
        setFeatureSearchValue("");
        setTrickSearch("");
        setTrickAttribute("");
        setFilteredTricks([]);
        setShowFilterOverlay(false);
        setShowFeatureOptions(false);
        setShowTrickAttributeOptions(false);
        setShowTricks(false);
        setFilterSearch("");
    }

    const closeFilter = () => {
        setShowFilterOverlay(false);
        setShowFeatureOptions(false);
        setShowTrickAttributeOptions(false);
    }

    // TODO: maybe actually call the filter api here?
    const filterTricks = async (sportFilter: string, sportDisplayFilter: string, featureFilter: string, trickSearchFilter: string, filteredTricksFilter: string[], setShowFilterOverlayFilter: boolean, setShowFeatureOptionsFilter: boolean, setShowTrickAttributeOptionsFilter: boolean) => {
        setInitLoading(true);
        setTrickSearchResults([]);
        setShowFilterOverlay(setShowFilterOverlayFilter);  // hide the filter buttons
        setShowFeatureOptions(setShowFeatureOptionsFilter);
        setShowTrickAttributeOptions(setShowTrickAttributeOptionsFilter);
        setPage(0);

        const searchSport = sportFilter != "" ? sportDisplayFilter : "";
        const searchFeature = featureFilter != "" ? featureFilter : "";
        const searchValue = trickSearchFilter != "" && trickSearchFilter != null ? trickSearchFilter : "";
        const searchTags = filteredTricksFilter.length > 0 ? filteredTricksFilter.map((x, i) => x.replace("_", " ")) : [];

        // Combine all tags into a single array
        const allTags = [
            ...searchTags,
            ...(searchFeature ? [searchFeature] : []),
            ...(searchSport ? [searchSport] : [])
        ].filter(tag => tag && tag.trim() !== "");

        let res: UserTrickSearchMetadata[];

        // Determine which API to call based on presence of tags
        if (allTags.length > 0) {
            // Use tags API with optional text
            const textParam = searchValue && searchValue.trim() !== "" ? searchValue.trim() : null;
            console.log("Filtering with tags:", allTags, "and text:", textParam);
            res = await SearchService.searchUserTrickWithTags(allTags, textParam, pageSize, 0, state.user.id);
            // Set filterSearch to indicate we're using tag-based search
            setFilterSearch(JSON.stringify({ tags: allTags, text: textParam }));
        } else if (searchValue && searchValue.trim() !== "") {
            // Use regular text-only API
            console.log("Filtering with text only:", searchValue);
            res = await SearchService.searchUserTrickPaginated(searchValue, pageSize, 0, state.user.id);
            setFilterSearch("");
        } else {
            // No search criteria
            res = [];
            setFilterSearch("");
        }

        res = await processTrickSearchForBlockedUsers(res || []);
        setTrickSearchResults(res);
        setPage(1); // finished getting page 0, now on page 1
        setInitLoading(false);
    }

    const handleTagDelete = async (tag: string) => {
        trackEvent('filter_tag_deleted', {
            tag: tag,
            remaining_tags: filteredTricks.filter(obj => obj !== tag),
            sport: sport,
            feature: feature
        });

        console.log("Deleting tag: " + tag);
        const filteredTricksValue = filteredTricks.filter(obj => {
            return obj !== tag
        });
        console.log("Filtered tricks: " + filteredTricksValue);
        setFilteredTricks(filteredTricksValue);
        if (filteredTricksValue.length === 0) {
            console.log("No more tags, clearing filters");
            clearFilters();
            closeFilter();
            await setInitExploreTricks();
        } else {
            await filterTricks(sport, sportDisplay, featureSearchValue, trickSearch, filteredTricksValue, showFilterOverlay, showFeatureOptions, showTrickAttributeOptions);
        }
    }

    const handleSearchDelete = async (search: string) => {
        trackEvent('filter_search_deleted', {
            search_term: search,
            current_filters: filteredTricks
        });

        const trickSearchValue = "";
        setTrickSearch(trickSearchValue);
        history.replace('/search');
        await filterTricks(sport, sportDisplay, featureSearchValue, trickSearchValue, filteredTricks, showFilterOverlay, showFeatureOptions, showTrickAttributeOptions);
    }

    const handleSportChange = async (sport: string, resetFilters: boolean) => {
        trackEvent('filter_sport_changed', {
            sport: sport,
            reset_filters: resetFilters,
            previous_sport: sportDisplay
        });

        const filterSport = sport
        let sportDisplayValue = "";
        if (sport === 'ski') {
            sportDisplayValue = SportType.SKIING
            setSportDisplay(sportDisplayValue);
        }

        if (sport === 'board') {
            sportDisplayValue = SportType.SNOWBOARDING
            setSportDisplay(sportDisplayValue);
        }

        if (sport === 'other') {
            sportDisplayValue = 'Other'
            setSportDisplay(sportDisplayValue);
        }

        console.log("Sport: " + filterSport);

        let featureValue = feature;
        let featureSearchFilterValue = featureSearchValue;
        let filteredTricksValue: string[] = filteredTricks;
        let trickAttributeValue = trickAttribute;

        let showFilterOverlayValue = true;
        let showFeatureOptionsValue = showFeatureOptions;
        let showTrickAttributeOptionsValue = showTrickAttributeOptions;

        if (resetFilters) {
            featureValue = "";
            featureSearchFilterValue = "";
            filteredTricksValue = [];
            trickAttributeValue = "";
        }

        console.log("sport changed");

        setSport(filterSport);
        setSportDisplay(sportDisplayValue);
        setFeature(featureValue);
        setFeatureSearchValue(featureSearchFilterValue);
        setTrickAttribute(trickAttributeValue);
        console.log("Filtered tricks: " + filteredTricksValue);
        setFilteredTricks(filteredTricksValue);
        setShowFilterOverlay(showFilterOverlayValue);
        setShowFeatureOptions(showFeatureOptionsValue);
        setShowTrickAttributeOptions(showTrickAttributeOptionsValue);
        setShowTricks(false);
        await filterTricks(filterSport, sportDisplayValue, featureSearchValue, trickSearch, filteredTricksValue, showFilterOverlayValue, showFeatureOptionsValue, showTrickAttributeOptionsValue);
    }

    // had to put these 2 functions in this class so that Feature options are shown when Trick Attribute button is clicked
    const setShowFeatures = (show: boolean) => {
        setShowFeatureOptions(show);
        if (show) {
            setShowTrickAttributeOptions(false);
            setShowTricks(false); // hide the tricks sidebar
        }
    }

    const setShowTrickAttribute = (show: boolean, trickAttribute: string) => {
        setShowTrickAttributeOptions(show);
        if (show) {
            console.log("setting show features to false");
            setShowFeatureOptions(false);
            if (!trickAttribute) {
                setShowTricks(false);
            }
        } else if (trickAttribute) {
            setShowTricks(true);
        }
    }

    const handleFilterFindTrickDisplay = async () => {
        trackEvent('filter_overlay_opened', {
            trigger: sport ? 'sport_change' : 'initial_open',
            current_sport: sport
        });

        setTrickSearch("");

        if (sport != "") {
            await handleSportChange(sport, false);
        } else {
            setShowFilterOverlay(true);
        }
    }


    const handleBack = () => {
        trackEvent('filter_navigation', {
            action: 'back',
            has_history: Boolean(location.key)
        });

        if (location.key) {
            console.log("Back button clicked");
            history.go(-1);
        } else {
            history.push('/home')
        }
    }

    // TODO: add a toolbar with the filter button and the existing filters in a horizontal scroll view (like the trick tags)

    if (!isAuthenticated && isNative) {
        return (
            <IonPage>
                <CustomHelmet title={"Ecliptic // Search"}
                    description={"View the tricks and clips on Ecliptic. Search for any trick ever - index by location, rider, trick name, trick attributes, and more"}
                    url={`https://ecliptic.day/search`} />
                <IonContent>
                    <ToolbarSearchBar></ToolbarSearchBar>
                    <div className="m-4"><LoginButton title="Sign up to search on Ecliptic :)" upload={false}
                        profile={true} /></div>
                </IonContent>
            </IonPage>
        );
    } else if (!sport && displayClips) {
        return (
            <IonPage>
                <CustomHelmet title={"Ecliptic // Search"}
                    description={"View the tricks and clips on Ecliptic. Search for any trick ever - index by location, rider, trick name, trick attributes, and more"}
                    url={`https://ecliptic.day/search`} />
                <FilterToolbar handleBack={handleBack} onTrickSearch={onTrickSearch} onProfileSearch={handleSearch}
                    onLocationSearch={onLocationSearch} onDisplayClips={onSelectClips} onDisplayRiders={onSelectRiders} onDisplayMap={onSelectMap}
                    displayClips={displayClips} displayRiders={displayProfiles} displayMap={displayMap} searchValue={trickSearch} />
                <SearchBarFilter searchValue={trickSearch} filters={filteredTricks} tagCallback={handleTagDelete}
                    sport={sportDisplay} feature={feature} searchCallback={handleSearchDelete}
                    sportCallback={handleSportChange}></SearchBarFilter>
                <IonContent>
                    <FilterTrickButton showFilterOverlay={showFilterOverlay}
                        handleFilterFindTrickDisplay={handleFilterFindTrickDisplay} />
                    {
                        initLoading && trickSearchResults.length === 0 ?
                            <SearchTricksLoading />
                            : (trickSearchResults.length === 0 && (trickSearch || filterSearch)) ?
                                <div className="flex flex-col items-center justify-center p-8 mt-16">
                                    <IonCard className="w-full max-w-md">
                                        <IonCardHeader>
                                            <IonCardTitle className="text-center text-xl">
                                                No clips found for "{getSearchDisplayText()}"
                                            </IonCardTitle>
                                        </IonCardHeader>
                                        <IonCardContent>
                                            <p className="text-center mb-4">
                                                Be the first to upload this trick to Ecliptic! 🎿🏂
                                            </p>
                                        </IonCardContent>
                                    </IonCard>
                                </div>
                                : <div className="h-full">
                                    <TrickSearchCardHolder
                                        trickArray={trickSearchResults.length > 0 ? trickSearchResults : exploreTrickSearchResults}
                                        onTrickSelection={onTrickSelection} loggedInId={state.user.id} trickSearch={trickSearch}
                                        onLoadMore={() => getMoreSearchTricks()}
                                        isLoadingMore={isLoadingMoreTricks} />
                                </div>
                    }
                    {showFilterOverlay &&
                        <div>
                            <div className="fixed left-0 top-0">
                                <div
                                    className={`relative ${isNative ? (platform === 'android' ? (filteredTricks.length > 0 ? "top-40" : "top-28") : (filteredTricks.length > 0 ? "top-52" : "top-44")) : (filteredTricks.length > 0 ? "top-40" : "top-28")}`}>
                                    <SportSelect selectSport={handleSportChange} sport={sport} />
                                </div>
                            </div>
                            <div className="fixed right-0 bottom-16">
                                <ExitFilterButton closeFilter={clearFilters} name="Clear" buttonType="clear-button" />
                            </div>
                            <div className="fixed right-0 bottom-0">
                                <ExitFilterButton closeFilter={closeFilter} name="Close"
                                    buttonType="search-filter-button" />
                            </div>
                        </div>
                    }
                </IonContent>
            </IonPage>
        );
    } else if (sport && !feature && displayClips) {
        return (
            <IonPage>
                <CustomHelmet title={"Ecliptic // Search"}
                    description={"View the tricks and clips on Ecliptic. Search for any trick ever - index by location, rider, trick name, trick attributes, and more"}
                    url={`https://ecliptic.day/search`} />
                <FilterToolbar handleBack={handleBack} onTrickSearch={onTrickSearch} onProfileSearch={handleSearch}
                    onLocationSearch={onLocationSearch} onDisplayClips={onSelectClips} onDisplayRiders={onSelectRiders} onDisplayMap={onSelectMap}
                    displayClips={displayClips} displayRiders={displayProfiles} displayMap={displayMap} searchValue={trickSearch} />
                <SearchBarFilter searchValue={trickSearch} filters={filteredTricks} tagCallback={handleTagDelete}
                    sport={sportDisplay} feature={feature} searchCallback={handleSearchDelete}
                    sportCallback={handleSportChange}></SearchBarFilter>
                <IonContent>
                    <FilterTrickButton showFilterOverlay={showFilterOverlay}
                        handleFilterFindTrickDisplay={handleFilterFindTrickDisplay} />
                    {
                        initLoading && trickSearchResults.length === 0 ?
                            <SearchTricksLoading />
                            : (trickSearchResults.length === 0 && (trickSearch || filterSearch)) ?
                                <div className="flex flex-col items-center justify-center p-8 mt-16">
                                    <IonCard className="w-full max-w-md">
                                        <IonCardHeader>
                                            <IonCardTitle className="text-center text-xl">
                                                No clips found for "{getSearchDisplayText()}"
                                            </IonCardTitle>
                                        </IonCardHeader>
                                        <IonCardContent>
                                            <p className="text-center mb-4">
                                                Be the first to upload this trick to Ecliptic! 🎿🏂
                                            </p>
                                        </IonCardContent>
                                    </IonCard>
                                </div>
                                : <div className="h-full">
                                    <TrickSearchCardHolder
                                        trickArray={trickSearchResults.length > 0 ? trickSearchResults : exploreTrickSearchResults}
                                        onTrickSelection={onTrickSelection} loggedInId={state.user.id} trickSearch={trickSearch}
                                        onLoadMore={() => getMoreSearchTricks()}
                                        isLoadingMore={isLoadingMoreTricks} />
                                </div>
                    }
                    {showFilterOverlay &&
                        <>
                            <div className="fixed left-0 top-0">
                                <div
                                    className={`relative ${isNative ? (platform == 'android' ? "top-40" : "top-52") : "top-40"}`}>
                                    <FeatureSelect sport={sport} feature={feature} selectFeature={onFeatureSelect}
                                        showOptions={showFeatureOptions}
                                        setShowFeatureOptions={setShowFeatures} />
                                </div>
                            </div>
                            <div className="fixed right-0 bottom-16">
                                <ExitFilterButton closeFilter={clearFilters} name="Clear" buttonType="clear-button" />
                            </div>
                            <div className="fixed right-0 bottom-0">
                                <ExitFilterButton closeFilter={closeFilter} name="Close"
                                    buttonType="search-filter-button" />
                            </div>
                        </>
                    }
                </IonContent>
            </IonPage>
        )
    }

    // Select Trick Attribute and then show the dropdown
    return (
        <IonPage>
            <CustomHelmet title={"Ecliptic // Search"}
                description={"View the tricks and clips on Ecliptic. Search for any trick ever - index by location, rider, trick name, trick attributes, and more"}
                url={`https://ecliptic.day/search`} />
            <FilterToolbar handleBack={handleBack} onTrickSearch={onTrickSearch} onProfileSearch={handleSearch}
                onLocationSearch={onLocationSearch} onDisplayClips={onSelectClips} onDisplayRiders={onSelectRiders} onDisplayMap={onSelectMap} displayClips={displayClips}
                displayRiders={displayProfiles} displayMap={displayMap} searchValue={trickSearch} />
            {
                displayClips ?
                    <>
                        <IonContent>
                            <SearchBarFilter searchValue={trickSearch} filters={filteredTricks}
                                tagCallback={handleTagDelete}
                                sport={sportDisplay} feature={feature} searchCallback={handleSearchDelete}
                                sportCallback={handleSportChange}></SearchBarFilter>
                            <FilterTrickButton showFilterOverlay={showFilterOverlay}
                                handleFilterFindTrickDisplay={handleFilterFindTrickDisplay} />
                            {initLoading && trickSearchResults.length === 0 ?
                                <SearchTricksLoading />
                                : (trickSearchResults.length === 0 && (trickSearch || filterSearch)) ?
                                    <div className="flex flex-col items-center justify-center p-8 mt-16">
                                        <IonCard className="w-full max-w-md">
                                            <IonCardHeader>
                                                <IonCardTitle className="text-center text-xl">
                                                    No clips found for "{getSearchDisplayText()}"
                                                </IonCardTitle>
                                            </IonCardHeader>
                                            <IonCardContent>
                                                <p className="text-center mb-4">
                                                    Be the first to upload this trick to Ecliptic! 🎿🏂
                                                </p>
                                            </IonCardContent>
                                        </IonCard>
                                    </div>
                                    : <div className="h-full">
                                        <TrickSearchCardHolder
                                            trickArray={trickSearchResults.length > 0 ? trickSearchResults : exploreTrickSearchResults}
                                            onTrickSelection={onTrickSelection} loggedInId={state.user.id}
                                            trickSearch={trickSearch}
                                            onLoadMore={() => getMoreSearchTricks()}
                                            isLoadingMore={isLoadingMoreTricks} />
                                    </div>}
                            {showFilterOverlay &&
                                <>
                                    <div className="fixed left-0 top-0">
                                        <div
                                            className={`absolute ${isNative ? (platform == 'android' ? "top-40" : "top-52") : "top-40"}`}>
                                            <FeatureSelect sport={sport} feature={feature}
                                                selectFeature={onFeatureSelect}
                                                showOptions={showFeatureOptions}
                                                setShowFeatureOptions={setShowFeatures} />
                                        </div>
                                    </div>
                                    <div className="fixed left-0 bottom-0">
                                        <TrickAttributeSelect sport={sport} feature={feature}
                                            trickAttribute={trickAttribute}
                                            selectTrickAttribute={onTrickAttributeSelect}
                                            showOptions={showTrickAttributeOptions}
                                            setShowTrickAttributeOptions={setShowTrickAttribute} />
                                    </div>
                                    {showTricks &&
                                        <TrickDropdown sport={sport} feature={feature} trickAttribute={trickAttribute}
                                            addTrickToFilter={addTrickToFilter}
                                            removeTrickFromFilter={removeTrickFromFilter}
                                            selectedTricks={filteredTricks} />}
                                    {showTricks ?
                                        <div>
                                            <div className="fixed left-0 bottom-32">
                                                <ExitFilterButton closeFilter={clearFilters} name="Clear"
                                                    buttonType="clear-button" />
                                            </div>
                                            <div className="fixed left-0 bottom-16">
                                                <ExitFilterButton closeFilter={closeFilter} name="Close"
                                                    buttonType="search-filter-button" />
                                            </div>
                                        </div>
                                        :
                                        <div>
                                            <div className="fixed right-0 bottom-16">
                                                <ExitFilterButton closeFilter={clearFilters} name="Clear"
                                                    buttonType="clear-button" />
                                            </div>
                                            <div className="fixed right-0 bottom-0">
                                                <ExitFilterButton closeFilter={closeFilter} name="Close"
                                                    buttonType="search-filter-button" />
                                            </div>
                                        </div>}
                                </>}
                        </IonContent>
                    </>
                    :
                    displayProfiles ?
                        (
                            (isLoadingProfiles || trickSearch?.length == 0 || trickSearch == undefined)
                                ? <UserListSkeleton /> :
                                <IonContent scrollEvents={true}>
                                    <div>
                                        <VirtualProfileSearchList
                                            profileSearchResults={profileSearchResults}
                                            onClickPersonProfile={onClickPersonProfile}
                                            trickSearch={trickSearch}
                                        />
                                        <IonInfiniteScroll
                                            onIonInfinite={async (ev) => {
                                                await fetchNextProfilePage();
                                                ev.target.complete();
                                            }}
                                            threshold="10%"
                                            disabled={!hasNextProfilePage}
                                        >
                                            <IonInfiniteScrollContent loadingSpinner="bubbles" />
                                        </IonInfiniteScroll>
                                    </div>
                                </IonContent>
                        )
                        : displayMap ?
                            <IonContent fullscreen className="ion-padding-0">
                                <WorldWeb
                                    key="filter-map"
                                    lat={getMapCoordinates().lat}
                                    long={getMapCoordinates().lng}
                                    zoom={baseZoom}
                                    geojson={geoJson}
                                    onClick={onTrickButtonClick}
                                    blurred={blurredMap}
                                    flyTo={!!flyToLocation}
                                    flyToCoords={flyToLocation}
                                    flyToZoom={flyToZoom}
                                    onBoundsChange={handleBoundsChange}
                                    onUserLocationUpdate={handleUserLocationUpdate}
                                    isNative={isNative}
                                    isSubscribed={isSubscribed}
                                />
                                {isAuthenticated && blurredMap &&
                                    <div className="z-100 fixed top-1/4 left-1/2 transform -translate-x-1/2 w-11/12 max-w-2xl mx-auto" style={{ zIndex: 100 }}>
                                        <IonCard className="mx-4">
                                            <IonCardHeader className="px-6 py-4">
                                                <IonCardTitle className="text-center text-xl">
                                                    You must be subscribed to the backcountry or tow rope pass to view the map and view spots from around the world. Go to the shop to purchase! Upload for access too!
                                                </IonCardTitle>
                                            </IonCardHeader>
                                            <IonCardContent className="px-6 py-4">
                                                <div className="flex flex-col gap-3 max-w-md mx-auto">
                                                    <IonButton expand="block" size="large" color="favorite" onClick={onGoToShop}>
                                                        Visit Shop
                                                    </IonButton>
                                                    <IonButton expand="block" size="large" color="secondary" onClick={reloadAuthInfo}>
                                                        Reload
                                                    </IonButton>
                                                </div>
                                            </IonCardContent>
                                        </IonCard>
                                    </div>
                                }
                                {!isAuthenticated && blurredMap &&
                                    <div className="z-100 fixed top-1/4 left-1/2 transform -translate-x-1/2 w-11/12 max-w-2xl mx-auto" style={{ zIndex: 100 }}>
                                        <IonCard className="mx-4">
                                            <IonCardHeader className="px-6 py-4">
                                                <IonCardTitle className="text-center text-xl">
                                                    You must be subscribed to the backcountry or tow rope pass to view the map and view spots from around the world. Go to the shop to purchase! Upload for access too!
                                                </IonCardTitle>
                                            </IonCardHeader>
                                            <IonCardContent className="px-6 py-4">
                                                <div className="flex flex-col gap-3 max-w-md mx-auto">
                                                    <IonButton expand="block" size="large" color="favorite" onClick={onGoToShop}>
                                                        Visit Shop
                                                    </IonButton>
                                                    <IonButton expand="block" size="large" color="secondary" onClick={reloadAuthInfo}>
                                                        Reload
                                                    </IonButton>
                                                </div>
                                            </IonCardContent>
                                        </IonCard>
                                    </div>
                                }
                            </IonContent>
                            : null
            }
        </IonPage>
    );
};

export default Filter;
